import useTranslation from 'next-translate/useTranslation';
import type { ContestSettings } from '@betterpool/api-types/contests-service';
import {
  ResizingMode,
  SlateLabelEnum,
  ContestSegmentType,
} from '@betterpool/api-types/contests-service';
import { IconInfoCircle } from '@tabler/icons-react';
import { Flex, createStyles, useMantineTheme } from '@mantine/core';
import Trans from 'next-translate/Trans';

import dt from '~/testing';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';
import { BadgeGuaranteed, BadgeResizable } from '~/components/shared/Badges/Badges';
import { Match } from '~/domains/common/components/Match/Match';
import { Text } from '~/domains/design-system/Text';
import {
  type Contest,
  AdditionalPayoutType,
} from '~/domains/contest/domains/services/interfaces/contest.interface';
import { useAdditionalPayoutSettings } from '~/domains/contest/domains/common/hooks/useAdditionalPayoutSettings';
import GuaranteedPlusBadge from '~/domains/design-system/badges/GuaranteedPlusBadge/GuaranteedPlusBadge';

import { SectionTitle } from '../components';

type PrizesSegmentsTitleProps = {
  isSidePanel?: boolean;
  segments?: ContestSettings['segments'];
  slateLabel?: SlateLabelEnum;
  resizingMode: ResizingMode;
  contest: Contest;
};

const useStyles = createStyles({
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

function ResizingModeBadgeIcon({ resizingMode }: Pick<PrizesSegmentsTitleProps, 'resizingMode'>) {
  return (
    <Match value={resizingMode}>
      <Match.On value={[ResizingMode.RESIZABLE]}>
        <Flex align="center" /* eslint-disable-line local-rules/use-theme-tokens */ gap={8}>
          <BadgeResizable iconSize={14} />
        </Flex>
      </Match.On>
      <Match.On value={[ResizingMode.GUARANTEED]}>
        <Flex align="center" /* eslint-disable-line local-rules/use-theme-tokens */ gap={8}>
          <BadgeGuaranteed iconSize={14} />
        </Flex>
      </Match.On>
    </Match>
  );
}

function ResizableTooltip({
  resizingMode,
  isGuaranteedPlus,
}: {
  resizingMode: ResizingMode;
  isGuaranteedPlus?: boolean;
}) {
  const translationKey = isGuaranteedPlus
    ? 'contest:detail.prizes.tooltip.guaranteedPlus'
    : `contest:detail.prizes.tooltip.${resizingMode.toLowerCase()}`;

  const icon = isGuaranteedPlus ? (
    <GuaranteedPlusBadge size="micro" />
  ) : (
    <ResizingModeBadgeIcon resizingMode={resizingMode} />
  );

  return (
    <Trans
      i18nKey={translationKey}
      components={{
        b: <b />,
        icon,
        flex: <Flex align="center" /* eslint-disable-line local-rules/use-theme-tokens */ gap={8} />,
        text: <Text variant="body-medium" />,
      }}
    />
  );
}

function PrizesSegmentsTitle({
  slateLabel,
  isSidePanel,
  segments,
  resizingMode,
  contest,
}: PrizesSegmentsTitleProps) {
  const { t } = useTranslation('contest');
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { type, isEnabled: isGuaranteedPlusEnabled } = useAdditionalPayoutSettings(contest);
  const isGuaranteedPlus = isGuaranteedPlusEnabled && type === AdditionalPayoutType.GUARANTEED_PLUS;

  const isSegmentedPayout = segments?.segmentType === ContestSegmentType.MULTI_SLATE;
  const isSlatePayout = segments?.segmentType === ContestSegmentType.SINGLE_SLATE;

  return (
    <SectionTitle
      className={classes.title}
      data-test-id={dt.contestDetail.section.prizes.title}
      isSidePanel={isSidePanel}
    >
      {t('detail.prizes.title')}
      <Tooltip
        withinPortal
        withArrow
        multiline
        labels={[
          {
            label: (
              <>
                <Trans
                  i18nKey="contest:detail.prizes.tooltip.segmentedPayouts"
                  components={{
                    b: <b />,
                    br: <br />,
                    text: <Text variant="body-medium" />,
                  }}
                />
                <ResizableTooltip resizingMode={resizingMode} />
              </>
            ),
            condition: isSegmentedPayout,
          },
          {
            label: (
              <>
                <Trans
                  i18nKey="contest:detail.prizes.tooltip.payoutSlatesWeek"
                  components={{
                    b: <b />,
                    br: <br />,
                    text: <Text variant="body-medium" />,
                  }}
                />
                <ResizableTooltip resizingMode={resizingMode} />
              </>
            ),
            condition: isSlatePayout && slateLabel === SlateLabelEnum.WEEK,
          },
          {
            label: (
              <>
                <Trans
                  i18nKey="contest:detail.prizes.tooltip.payoutSlatesTournament"
                  components={{
                    b: <b />,
                    br: <br />,
                    text: <Text variant="body-medium" />,
                  }}
                />
                <ResizableTooltip resizingMode={resizingMode} />
              </>
            ),
            condition: isSlatePayout && slateLabel === SlateLabelEnum.TOURNAMENT,
          },
          {
            label: (
              <>
                <Trans
                  i18nKey="contest:detail.prizes.tooltip.payoutSlatesSlate"
                  components={{
                    b: <b />,
                    br: <br />,
                    text: <Text variant="body-medium" />,
                  }}
                />
                <ResizableTooltip resizingMode={resizingMode} />
              </>
            ),
            condition:
              (isSlatePayout && slateLabel === SlateLabelEnum.SLATE) ||
              (isSlatePayout && !slateLabel),
          },
          {
            label: (
              <>
                <Trans
                  i18nKey="contest:detail.prizes.tooltip.overallPayouts"
                  components={{
                    b: <b />,
                    br: <br />,
                    text: <Text variant="body-medium" />,
                  }}
                />
                <ResizableTooltip resizingMode={resizingMode} isGuaranteedPlus={isGuaranteedPlus} />
              </>
            ),
          },
        ]}
      >
        <IconInfoCircle cursor="pointer" color={theme.colors.gray[6]} />
      </Tooltip>
    </SectionTitle>
  );
}

export default PrizesSegmentsTitle;
